import React from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../data.js';
import { ASSETS } from '../assetManifest.js';

type MainMenuProps = {
  onStartGame: () => void;
  onOpenAudioSettings: () => void;
  onOpenCollections: () => void;
  onOpenCredits: () => void;
};

export const MainMenu: React.FC<MainMenuProps> = ({
  onStartGame,
  onOpenAudioSettings,
  onOpenCollections,
  onOpenCredits,
}) => {
  const mainMenuBackground = ASSETS.backgrounds?.main_menu?.path;

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{
        backgroundImage: mainMenuBackground ? `url(${mainMenuBackground})` : 'linear-gradient(135deg, #1a1611 0%, #2d2419 50%, #1a1611 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/60" />

      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, ${CONSTANTS.ui.theme.colors.primary}40 0%, transparent 50%), radial-gradient(circle at 75% 75%, ${CONSTANTS.ui.theme.colors.accent}30 0%, transparent 50%)`
      }} />
      
      <motion.div
        className="relative z-10 flex flex-col items-center p-12 w-[500px] max-w-[90vw]"
        style={{
          background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.15) 0%, rgba(218, 165, 32, 0.1) 50%, rgba(139, 69, 19, 0.15) 100%)',
          border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
          borderRadius: '20px',
          boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Decorative corner elements */}
        <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 rounded-tl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 rounded-tr-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 rounded-bl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 rounded-br-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />

        <motion.h1 
          className="font-cinzel text-6xl font-bold mb-4 text-center tracking-wider"
          style={{ 
            color: CONSTANTS.ui.theme.colors.text,
            textShadow: CONSTANTS.ui.theme.effects.textGlow,
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 50%, ${CONSTANTS.ui.theme.colors.primary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {CONSTANTS.gameTitle}
        </motion.h1>

        <motion.p 
          className="font-crimson text-lg mb-8 text-center italic"
          style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          Master the Elements Through Time
        </motion.p>

        <div className="flex flex-col gap-4 w-full">
          <motion.button
            onClick={onStartGame}
            className="font-crimson w-full px-8 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
              color: CONSTANTS.ui.theme.colors.background,
              border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
              boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
            }}
            whileHover={{ 
              boxShadow: `0 6px 12px rgba(0, 0, 0, 0.4), 0 12px 24px rgba(218, 165, 32, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)` 
            }}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.3 }}
          >
            History Mode
          </motion.button>
          
          <motion.button
            onClick={onOpenCollections}
            className="font-crimson w-full px-8 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
              color: CONSTANTS.ui.theme.colors.text,
              border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
              boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
            }}
            whileHover={{ 
              boxShadow: `0 6px 12px rgba(0, 0, 0, 0.4), 0 12px 24px rgba(139, 69, 19, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)` 
            }}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.3 }}
          >
            Collections
          </motion.button>
          
          <motion.button
            onClick={onOpenAudioSettings}
            className="font-crimson w-full px-8 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
              color: CONSTANTS.ui.theme.colors.text,
              border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
              boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
            }}
            whileHover={{ 
              boxShadow: `0 6px 12px rgba(0, 0, 0, 0.4), 0 12px 24px rgba(139, 69, 19, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)` 
            }}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.3 }}
          >
            Audio Settings
          </motion.button>

          <motion.button
            onClick={onOpenCredits}
            className="font-crimson w-full px-8 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
              color: CONSTANTS.ui.theme.colors.text,
              border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
              boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
            }}
            whileHover={{ 
              boxShadow: `0 6px 12px rgba(0, 0, 0, 0.4), 0 12px 24px rgba(139, 69, 19, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)` 
            }}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.3 }}
          >
            Credits
          </motion.button>
        </div>

        {/* Decorative bottom element */}
        <motion.div 
          className="mt-8 w-32 h-1 rounded-full"
          style={{ background: `linear-gradient(90deg, transparent 0%, ${CONSTANTS.ui.theme.colors.primary} 50%, transparent 100%)` }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        />
      </motion.div>
    </motion.div>
  );
};
